import * as React from "react";
import { cn } from "@/lib/utils";

export interface FloatingInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: boolean;
}

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  (
    { className, type, label, id, value, onFocus, onBlur, error, ...props },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const generatedId = React.useId();
    const inputId = id || generatedId;

    // Check if input has value - use controlled value prop if available
    const hasValue = typeof value === "string" ? value.length > 0 : !!value;
    const isFloating = isFocused || hasValue;

    return (
      <div className="relative">
        <input
          type={type}
          id={inputId}
          value={value}
          className={cn(
            "peer flex h-14 w-full rounded-xl border bg-muted/50 px-4 pt-5 pb-2 text-base ring-offset-background",
            "placeholder:text-transparent",
            "focus:outline-none focus:ring-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "transition-colors duration-150",
            error
              ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
              : "border-border/70 focus:border-bank-primary focus:ring-bank-primary/20",
            className,
          )}
          placeholder={label}
          ref={ref}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          {...props}
        />
        <label
          htmlFor={inputId}
          className={cn(
            "absolute left-4 transition-all duration-150 pointer-events-none",
            error ? "text-red-500" : "text-muted-foreground",
            isFloating
              ? "top-2 text-xs font-medium"
              : "top-1/2 -translate-y-1/2 text-base",
          )}
        >
          {label}
        </label>
      </div>
    );
  },
);
FloatingInput.displayName = "FloatingInput";

export { FloatingInput };
